import axios, { CanceledError } from "axios";
import router from "@/router";
import { billingUrl } from "@/utils/billing-url";
import { useToast } from "vue-toastification";
import { useAuthStore } from "@/state/pinia";
import { pinia } from "@/state/pinia";
import { useLocaleStore } from '@/state/pinia/locale'

const toast = useToast();

// Flag to prevent multiple error toasts during logout
let isHandlingAuthFailure = false;

/**
 * Helper function to check if an error should be silently ignored
 * (e.g., authentication errors during logout redirect)
 */
export const isAuthError = (error) => {
    return error instanceof CanceledError && error.message === "unauthenticated";
};

const api = axios.create({
    baseURL: process.env.VUE_APP_BE_URL,
    headers: { "Content-Type": "application/json" },
    timeout: 10000,
});

api.interceptors.request.use((config) => {
    let token = null;
    try {
        const auth = useAuthStore(pinia);
        token = auth.getToken?.();
    } catch {
        token = localStorage.getItem("auth_token");
    }
    config.headers = config.headers || {};
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    if (config.data instanceof FormData) {
        delete config.headers["Content-Type"];
    }

    const store = useLocaleStore()
    const lang = (store && store.get && store.get()) || store.lang || 'pl'
    config.headers['Accept-Language'] = lang
    config.headers['X-Lang'] = lang // opcjonalnie

    return config;
});

api.interceptors.response.use(
    (response) => {
        // Reset auth failure flag on successful request
        isHandlingAuthFailure = false;
        return response;
    },
    (error) => {
        const { data, status } = error?.response || {};

        if (status === 401) {
            // Prevent multiple toasts and redirects during logout
            if (!isHandlingAuthFailure) {
                isHandlingAuthFailure = true;

                // Clear all existing toasts to prevent error messages from showing
                toast.clear();

                const auth = useAuthStore(pinia);
                auth.logout();

                if (auth.isSsoEnabled()) {
                    auth.redirectToSso();
                } else if (router.currentRoute.value.path !== "/login") {
                    router.replace("/login").catch(() => {});
                }
            }

            return Promise.reject(new CanceledError("unauthenticated"));
        }

        if (status === 422 && data?.error === "plan_limit_exceeded") {
            // Wyższy plan kupuje się w panelu rozliczeń w SSO — tu tylko odsyłamy.
            const upgradeUrl = billingUrl();
            toast.info("Kliknij tutaj aby zwiększyć swoje limity!", {
                toastClassName: "upgrade-pointer",
                onClick: () => {
                    if (upgradeUrl) {
                        window.location.href = upgradeUrl;
                    } else {
                        router.push({ name: "Subscriptions" }).catch(() => {});
                    }
                },
            });
        }

        // 402 = nieopłacony abonament, panel działa tylko do odczytu.
        // Backend zwraca to na każdej próbie zapisu, więc komunikat musi
        // prowadzić prosto do rozliczeń — inaczej user tylko widzi, że
        // „nie da się zapisać", i nie wie dlaczego.
        if (status === 402) {
            toast.error(data?.message || "Abonament jest nieopłacony — zapisywanie jest wstrzymane.", {
                toastClassName: "upgrade-pointer",
                onClick: () => {
                    const url = billingUrl();
                    if (url) window.location.href = url;
                },
            });
            error._toastShown = true;
            return Promise.reject(error);
        }

        if (status === 428 && data?.code === "workspace_required") {
            const target = data?.redirect || "/workspaces/new";
            if (router.currentRoute.value.path !== target) {
                router.replace(target).catch(() => {});
            }
            return Promise.reject(new CanceledError("workspace_required"));
        }

        return Promise.reject(error);
    }
);

export default api;
